import { useCallback, useEffect, useState } from 'react';
import { useActiveConversationSelector } from '../../providers/active-conversation.provider';
import { useTeamChatSelector } from '../../providers/team-chat.provider';
import type { Target } from '../../utils';
import { ConversationBody } from './conversation-body';
import { ConversationMemberSelection } from './conversation-member-selection/conversation-member-selection';
import { ConversationTitle } from './conversation-title';

export const ConversationPanel = () => {
  const mainPanelView = useTeamChatSelector((ctx) => ctx.mainPanelView);
  const messages = useActiveConversationSelector((ctx) => ctx.messages);
  const fetchNextPage = useActiveConversationSelector((ctx) => ctx.fetchNextPage);
  const fetchPreviousPage = useActiveConversationSelector((ctx) => ctx.fetchPreviousPage);
  const hasNextPage = useActiveConversationSelector((ctx) => ctx.hasNextPage);
  const hasPreviousPage = useActiveConversationSelector((ctx) => ctx.hasPreviousPage);
  const isFetching = useActiveConversationSelector((ctx) => ctx.isFetching);
  const pendingDmTarget = useTeamChatSelector((ctx) => ctx.pendingDmTarget);
  const clearPendingDmTarget = useTeamChatSelector((ctx) => ctx.clearPendingDmTarget);
  const [selectedTarget, setSelectedTarget] = useState<Target>();
  const onTargetChange = useCallback((target: Target | undefined) => {
    setSelectedTarget(target);
  }, []);

  useEffect(() => {
    // Preselect a DM target staged by `openConversationWithUser` when opening a new (dynamic) conversation.
    if (mainPanelView === 'conversation-dynamic' && pendingDmTarget) {
      setSelectedTarget({ type: 'dm', users: [pendingDmTarget] });
      clearPendingDmTarget();
    }
  }, [mainPanelView, pendingDmTarget, clearPendingDmTarget]);

  return (
    <>
      <ConversationTitle />
      {mainPanelView === 'conversation-dynamic' && (
        <ConversationMemberSelection
          selectedTarget={selectedTarget}
          onChange={onTargetChange}
          // When a target is preselected via `openConversationWithUser`, `pendingDmTarget` is set at
          // the same render this mounts, so we suppress the auto-open dropdown. Normal user-initiated
          // new conversations have no pending target and keep the default open-on-mount behavior.
          openOnMount={!pendingDmTarget}
        />
      )}
      <ConversationBody
        selectedTarget={selectedTarget}
        messages={messages}
        fetchNextPage={fetchNextPage}
        fetchPreviousPage={fetchPreviousPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        isFetching={isFetching}
      />
    </>
  );
};
